import { singleFlightAccessRefresh } from './refresh-coordinator.js';
import { CanceledError, isAxiosError, isCancel, type AxiosError, type AxiosInstance } from "axios";
import { fromAxiosError, isApiError, isNetworkAxiosError } from "./errors.js";
import { parseFeatureDenied } from './feature-denied.js';
import type {
  ApiErrorResponse,
  CreateApiClientConfig,
  ExtendedAxiosRequestConfig,
} from "./types.js";

function assertRequestScope(request: ExtendedAxiosRequestConfig, config: CreateApiClientConfig): void {
  if (config.getRequestScope && request._sessionScope !== config.getRequestScope()) throw new CanceledError('Session context changed');
}

interface InterceptorState {
  isReconnecting: boolean;
  isRefreshing: boolean;
  unauthorizedFired: boolean;
  refreshInFlight: Promise<string | null> | null;
  refreshQueue: Array<{
    resolve: (token: string | null) => void;
    reject: (err: unknown) => void;
  }>;
}

function createState(): InterceptorState {
  return {
    isReconnecting: false,
    isRefreshing: false,
    unauthorizedFired: false,
    refreshInFlight: null,
    refreshQueue: [],
  };
}

function decodeJwtExp(token: string): number | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    // eslint-disable-next-line unicorn/prefer-string-replace-all -- este pacote compila para ES2020; String#replaceAll é ES2021
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4 || 4)) % 4);
    const payload = JSON.parse(
      typeof atob === "function"
        ? atob(padded)
        : Buffer.from(padded, "base64").toString("utf8"),
    ) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

function isJwtExpired(token: string): boolean {
  const exp = decodeJwtExp(token);
  if (exp === null) return false; // unknown / non-JWT — trust upstream
  return Date.now() >= exp * 1000;
}

function processQueue(
  state: InterceptorState,
  err: unknown | null,
  token: string | null,
) {
  for (const item of state.refreshQueue) {
    if (err) item.reject(err);
    else item.resolve(token);
  }
  state.refreshQueue = [];
}

/**
 * Dispara `onUnauthorized` no maximo uma vez por "rajada" de 401s, resetando a trava
 * apos um microtask (o mesmo padrao estava duplicado em 3 pontos do response
 * interceptor). Extraido tambem para reduzir a Cognitive Complexity da funcao que
 * lida com a resposta (sonarjs/cognitive-complexity, S3776).
 */
function notifyUnauthorizedOnce(
  state: InterceptorState,
  config: CreateApiClientConfig,
  error: AxiosError<ApiErrorResponse>,
): void {
  if (error.config) assertRequestScope(error.config as ExtendedAxiosRequestConfig, config);
  if (state.unauthorizedFired) return;
  state.unauthorizedFired = true;
  config.onUnauthorized?.(
    error.response?.data ?? null,
    error.response?.status ?? null,
  );
  queueMicrotask(() => {
    state.unauthorizedFired = false;
  });
}

function notifyResponseError(
  error: AxiosError<ApiErrorResponse>,
  original: ExtendedAxiosRequestConfig,
  config: CreateApiClientConfig,
): void {
  assertRequestScope(original, config);
  if (original._silentError || !error.response) return;
  const { data, status } = error.response;
  const denial = parseFeatureDenied(data, status);
  if (denial && config.onFeatureDenied) {
    config.onFeatureDenied(denial);
    return;
  }
  config.onError?.(data ?? null, status);
}

/**
 * Erro de rede (sem `error.response`) — retry com backoff exponencial ate
 * `maxRetries`, notificando `onConnectionLost`/`onConnectionRestored`/
 * `onConnectionFailed`. Extraido do handler de resposta (sonarjs/cognitive-
 * complexity, S3776) — mesma logica, so movida para funcao propria.
 */
async function retryAfterNetworkError(
  error: AxiosError<ApiErrorResponse>,
  original: ExtendedAxiosRequestConfig,
  state: InterceptorState,
  config: CreateApiClientConfig,
  maxRetries: number,
  baseDelay: number,
  instance: AxiosInstance,
) {
  assertRequestScope(original, config);
  const attempt = original._retryCount ?? 0;

  if (attempt < maxRetries) {
    original._retryCount = attempt + 1;

    if (!state.isReconnecting) {
      state.isReconnecting = true;
      config.onConnectionLost?.();
    }

    const delay = baseDelay * Math.pow(2, attempt);
    await new Promise((r) => setTimeout(r, delay));
    assertRequestScope(original, config);

    try {
      const result = await instance(original);
      assertRequestScope(original, config);
      // eslint-disable-next-line require-atomic-updates -- `state.isReconnecting` e liberado por um mutex manual: a aquisicao (`if (!state.isReconnecting) { state.isReconnecting = true; ... }` acima) e sincrona, sem await entre o check e o set, entao duas chamadas concorrentes nao podem os duas passar pela guarda. O eslint nao modela esse padrao de mutex e sinaliza falso-positivo.
      state.isReconnecting = false;
      config.onConnectionRestored?.();
      return result;
    } catch (retryErr) {
      return Promise.reject(retryErr);
    }
  }

  assertRequestScope(original, config);
  state.isReconnecting = false;
  config.onConnectionFailed?.();
  return Promise.reject(fromAxiosError(error));
}

/**
 * 401 com `refreshToken` configurado — mutex `state.isRefreshing` para so uma troca
 * de token em voo por vez; chamadas concorrentes entram na `refreshQueue` e sao
 * resolvidas juntas quando o refresh em andamento termina. Extraido do handler de
 * resposta (sonarjs/cognitive-complexity, S3776) — mesma logica, so movida.
 */
async function refreshTokenAndReplay(
  error: AxiosError<ApiErrorResponse>,
  original: ExtendedAxiosRequestConfig,
  state: InterceptorState,
  config: CreateApiClientConfig,
  refreshToken: () => Promise<string | null>,
  instance: AxiosInstance,
) {
  assertRequestScope(original, config);
  original._retry = true;

  if (state.isRefreshing) {
    return new Promise<string | null>((resolve, reject) => {
      state.refreshQueue.push({ resolve, reject });
    }).then((newToken) => {
      assertRequestScope(original, config);
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return instance(original);
      }
      throw fromAxiosError(error);
    }, (queueError: unknown) => { assertRequestScope(original, config); throw queueError; });
  }

  state.isRefreshing = true;
  try {
    const attempted = String(original.headers.Authorization ?? '').replace(/^Bearer /, '');
    const newToken = await singleFlightAccessRefresh(config.baseUrl, attempted, refreshToken, config.getToken);
    assertRequestScope(original, config);
    // eslint-disable-next-line require-atomic-updates -- mesmo mutex manual do `isReconnecting`: aquisicao sincrona (`if (state.isRefreshing) {...} state.isRefreshing = true;` acima) sem await entre check e set — chamadas concorrentes entram na `refreshQueue` em vez de disputar esta flag. Falso-positivo do eslint para esse padrao.
    state.isRefreshing = false;
    processQueue(state, null, newToken);

    if (newToken) {
      original.headers.Authorization = `Bearer ${newToken}`;
      return instance(original);
    }

    // refresh retornou null -> nao autorizado
    notifyUnauthorizedOnce(state, config, error);
    return Promise.reject(fromAxiosError(error));
  } catch (refreshErr) {
    state.isRefreshing = false;
    processQueue(state, refreshErr, null);
    assertRequestScope(original, config);
    const refreshStatus = isApiError(refreshErr) ? refreshErr.statusCode
      : isAxiosError(refreshErr) ? refreshErr.response?.status : undefined;
    if (refreshStatus === 401 || refreshStatus === 403) notifyUnauthorizedOnce(state, config, error);
    return Promise.reject(refreshErr);
  }
}

export function attachInterceptors(
  instance: AxiosInstance,
  config: CreateApiClientConfig,
): void {
  const maxRetries = config.maxRetries ?? 3;
  const baseDelay = config.retryDelayMs ?? 2000;
  const state = createState();

  // ---------- Request: token + extra headers ----------
  instance.interceptors.request.use(
    async (req) => {
      const scoped = req as ExtendedAxiosRequestConfig;
      if (config.getRequestScope) scoped._sessionScope ??= config.getRequestScope();
      assertRequestScope(scoped, config);
      let token = await config.getToken();
      assertRequestScope(scoped, config);

      // Proactive refresh: when the persisted access token has already
      // expired, exchange it before sending the request so the browser
      // never logs an avoidable 401. The response interceptor below still
      // handles invalidations that happen mid-session.
      const reqUrl = req.url || "";
      const isAuthEndpoint =
        reqUrl.includes("/auth/refresh") ||
        reqUrl.includes("/auth/login") ||
        reqUrl.includes("/auth/register") ||
        reqUrl.includes("/auth/logout");

      if (
        token &&
        !isAuthEndpoint &&
        config.refreshToken &&
        isJwtExpired(token)
      ) {
        if (!state.refreshInFlight) {
          state.refreshInFlight = singleFlightAccessRefresh(config.baseUrl, token, config.refreshToken, config.getToken)
            .catch(() => null)
            .finally(() => {
              state.refreshInFlight = null;
            });
        }
        const refreshed = await state.refreshInFlight;
        assertRequestScope(scoped, config);
        token = refreshed ?? (await config.getToken());
      }

      assertRequestScope(scoped, config);
      if (token) {
        // eslint-disable-next-line require-atomic-updates -- `req` e o parametro do interceptor do axios: uma instancia NOVA e propria por requisicao (nao e estado compartilhado entre chamadas concorrentes), entao nao ha o risco de "outdated state" que a regra assume ao ver leitura+escrita atravessadas por await.
        req.headers.Authorization = `Bearer ${token}`;
      }
      if (config.getExtraHeaders) {
        const extras = await config.getExtraHeaders(token ?? null);
        assertRequestScope(scoped, config);
        for (const [k, v] of Object.entries(extras)) {
          if (v !== undefined && v !== null) {
            req.headers[k] = v;
          }
        }
      }
      return req;
    },
    (err) => Promise.reject(err),
  );

  // ---------- Response: retry, 401, error mapping ----------
  instance.interceptors.response.use(
    (res) => { assertRequestScope(res.config as ExtendedAxiosRequestConfig, config); return res; },
    async (error: AxiosError<ApiErrorResponse>) => {
      // Cancelamentos NUNCA disparam callbacks nem mapping.
      if (isCancel(error)) {
        return Promise.reject(error);
      }

      const original = error.config as ExtendedAxiosRequestConfig | undefined;
      if (!original) {
        return Promise.reject(fromAxiosError(error));
      }

      assertRequestScope(original, config);
      // ---- Network error -> retry com backoff (exceto requisicao marcada `_noRetry`) ----
      if (!error.response && isNetworkAxiosError(error)) {
        if (original._noRetry) return Promise.reject(fromAxiosError(error));
        return retryAfterNetworkError(
          error,
          original,
          state,
          config,
          maxRetries,
          baseDelay,
          instance,
        );
      }

      // Outros erros sem response.
      if (!error.response) {
        return Promise.reject(fromAxiosError(error));
      }

      const status = error.response.status;
      const isRefreshEndpoint = original.url?.includes("/auth/refresh");

      // ---- 401 -> refresh + replay (com mutex) ----
      if (
        status === 401 &&
        !original._retry &&
        !isRefreshEndpoint &&
        config.refreshToken
      ) {
        return refreshTokenAndReplay(
          error,
          original,
          state,
          config,
          config.refreshToken,
          instance,
        );
      }

      // 401 sem refreshToken configurado -> apenas notificar uma vez.
      if (status === 401) {
        notifyUnauthorizedOnce(state, config, error);
      }

      const apiError = fromAxiosError(error);
      notifyResponseError(error, original, config);

      return Promise.reject(apiError);
    },
  );
}
